// Deep tradition content integrated from the Mahakali Studios tradition repositories
// (buddhism/realm-journey, shaktism/shakti-cosmic-journey, shaivism/shiva-cosmos-journey,
// sikhism/panth-path-finder). Summarised for the INDIC platform.

export const TRADITION_SOURCES = {
  buddhism: { site: "buddhism.mahakalistudios.com", label: "Realm Journey" },
  shaktism: { site: "shakti.mahakalistudios.com", label: "Shakti · Cosmic Journey" },
  shaivism: { site: "shiv.mahakalistudios.com", label: "Shiva · Cosmos Journey" },
  sikhism: { site: "waheguru.mahakalistudios.com", label: "Panth · Path Finder" },
};

export const COMPARE_DIMENSIONS = [
  { key: "reality", label: "Ultimate Reality" },
  { key: "self", label: "The Self" },
  { key: "problem", label: "The Human Problem" },
  { key: "path", label: "The Path / Practice" },
  { key: "goal", label: "The Goal" },
  { key: "text", label: "Central Text(s)" },
  { key: "world", label: "Stance toward the World" },
];

export const COMPARE = {
  buddhism: {
    reality: "No creator and no permanent substance; all things arise interdependently and are impermanent (anitya).",
    self: "Anatta — no fixed, separate self; the person is a changing stream of processes.",
    problem: "Dukkha — suffering born of craving and ignorance about the nature of things.",
    path: "The Eightfold Path: ethics, meditation, and analytical insight into impermanence and non-self.",
    goal: "Nirvana — the extinguishing of craving and the end of suffering.",
    text: "The Pali Canon, Prajñaparamita and Madhyamaka works, and vast commentarial traditions.",
    world: "The world is impermanent and conditioned; compassion for all beings, with detachment from clinging.",
  },
  shaktism: {
    reality: "Reality is the Goddess — Shakti, the dynamic power and energy underlying and animating all that is.",
    self: "The self is a localisation of divine power; realising it is recognising oneself as Shakti.",
    problem: "The sense of separation from the sacred power that one already is.",
    path: "Devotion, mantra, yantra, and tantric practice that treats the body as a sacred instrument.",
    goal: "Union with the Goddess — liberation experienced as living recognition (jivanmukti).",
    text: "Devi Mahatmya, the Tantras, and Shakta hymns and philosophical works.",
    world: "The world is the Goddess's real creative play (lila), to be transformed rather than merely renounced.",
  },
  shaivism: {
    reality: "Shiva as pure, self-luminous consciousness; in non-dual Shaivism the universe is its creative vibration (spanda).",
    self: "The self is Shiva in disguise; bondage is forgetting, liberation is recognition (pratyabhijna).",
    problem: "Limitation and forgetfulness — consciousness contracting and taking itself to be small and separate.",
    path: "Devotion, yoga, aesthetic and contemplative recognition; from dualist worship to non-dual insight.",
    goal: "Recognition of one's identity with Shiva — freedom as awakening, here and now.",
    text: "The Agamas, Shiva Sutras, and the works of Abhinavagupta and the Pratyabhijna school.",
    world: "The world is the real self-expression of consciousness — to be seen correctly, not escaped.",
  },
  sikhism: {
    reality: "Ik Onkar — one formless reality beyond division, immanent and transcendent, known through its Name.",
    self: "The soul longs to merge with the one reality; haumai (ego) is what separates it.",
    problem: "Haumai — self-centredness and forgetfulness of the divine Name and order (hukam).",
    path: "Naam simran (remembrance), honest work, and selfless service (seva) in community.",
    goal: "Union with the One — release from ego and the cycle of rebirth, living in harmony with hukam.",
    text: "The Guru Granth Sahib, honoured as the eternal living Guru.",
    world: "Full engagement with the world: the saint-soldier, family life, equality, and service — not withdrawal.",
  },
};

export const DEEP = {
  buddhism: [
    {
      kind: "cards",
      title: "The Six Realms of Rebirth",
      note: "The wheel of becoming (bhavacakra) — read both cosmologically and as states of mind.",
      items: [
        { name: "Naraka", script: "नरक", sub: "The Hell Realms", text: "States of extreme suffering from severely harmful karma — immense in duration, but never eternal. Also read as the phenomenology of rage and terror." },
        { name: "Preta", script: "प्रेत", sub: "Hungry Ghosts", text: "Beings of insatiable hunger — narrow throats, vast bellies. Widely read as the structure of addiction; generosity (dāna) is the counter-practice." },
        { name: "Tiryagyoni", script: "तिर्यग्योनि", sub: "The Animal Realm", text: "Existence governed by instinct, fear and survival — fully sentient and worthy of compassion. Non-harming (ahiṃsā) extends to all." },
        { name: "Manuṣya", script: "मनुष्य", sub: "Rare Opportunity", text: "The human realm mixes pleasure and pain in proportions that make awakening possible — the 'precious human birth'." },
        { name: "Asura", script: "असुर", sub: "The Realm of Rivalry", text: "Powerful beings consumed by envy and competition — abundance experienced as deprivation because another has more." },
        { name: "Deva", script: "देव", sub: "The Heavenly Realms", text: "Radiant beings of long life and pleasure. Their danger is not pain but distraction: even heaven is impermanent." },
      ],
    },
    {
      kind: "cards",
      title: "The Three Poisons",
      note: "At the hub of the wheel — the roots of suffering.",
      items: [
        { name: "Rāga / Lobha", script: "राग", sub: "Greed / Craving", text: "The pull toward what promises completion. It does not end by being fed. Depicted as a cock." },
        { name: "Dveṣa / Dosa", script: "द्वेष", sub: "Hatred / Aversion", text: "The push against what is. It narrows the world to a threat. Depicted as a snake." },
        { name: "Moha / Avidyā", script: "मोह", sub: "Delusion / Ignorance", text: "Not seeing clearly; the pig from whose mouth the other two emerge." },
      ],
    },
  ],
  shaktism: [
    {
      kind: "cards",
      title: "The Ten Mahāvidyās",
      note: "Ten forms of wisdom spanning the full spectrum from terror to grace.",
      items: [
        { name: "Kālī", script: "काली", sub: "She Who Confronts Time", text: "Time, and the darkness that remains when forms end. Confronting her image: what remains when every form the mind clings to is withdrawn?" },
        { name: "Tārā", script: "तारा", sub: "She Who Carries Across", text: "The ferrying one — the capacity of consciousness to traverse what it cannot survive intact." },
        { name: "Tripurasundarī", script: "त्रिपुरसुन्दरी", sub: "Beauty of the Three Worlds", text: "In Śrīvidyā, identical with the Śrī Chakra: goddess, geometry and sound as three registers of one reality." },
        { name: "Bhuvaneśvarī", script: "भुवनेश्वरी", sub: "Sovereign of the Worlds", text: "She figures ākāśa — space as the condition of manifestation: the room reality has to happen in." },
        { name: "Bhairavī", script: "भैरवी", sub: "The Ardent", text: "Tapas — heat as transformation; the ascetic fire that dissolves what it purifies." },
        { name: "Chinnamastā", script: "छिन्नमस्ता", sub: "The Severed Head", text: "Life, death and nourishment as a single circuit — a symbolic diagram of self-giving." },
        { name: "Dhūmāvatī", script: "धूमावती", sub: "She of Smoke", text: "A meditation on absence and impermanence; the sacred that refuses to be pleasant." },
        { name: "Bagalāmukhī", script: "बगलामुखी", sub: "She Who Stills", text: "Stambhana — the pause in which reaction stops and choice becomes possible." },
        { name: "Mātaṅgī", script: "मातङ्गी", sub: "Speech from the Margins", text: "The goddess of language and music arriving from outside the sanctioned centre." },
        { name: "Kamalā", script: "कमला", sub: "The Lotus Goddess", text: "Abundance as a face of the Goddess — flourishing, beauty and sustenance." },
      ],
    },
    {
      kind: "cards",
      title: "The Grammar of Śakti",
      note: "Non-dual Śākta philosophy: consciousness and its power.",
      items: [
        { name: "Cit", script: "चित्", sub: "Consciousness", text: "Self-luminous awareness — the light in which anything is anything at all." },
        { name: "Śakti", script: "शक्ति", sub: "Power", text: "Reflective self-awareness (vimarśa). Not a faculty Śiva possesses but the reality of which Śiva is the still aspect." },
        { name: "Māyā", script: "माया", sub: "Appearance", text: "A real creative power of the Goddess, not sheer illusion. What is illusory is the sense of separation, not the world." },
        { name: "Pratyabhijñā", script: "प्रत्यभिज्ञा", sub: "Recognition", text: "Liberation as living recognition (jīvanmukti): the world read correctly, as Śakti." },
      ],
    },
  ],
  shaivism: [
    {
      kind: "cards",
      title: "The Forms of Śiva",
      note: "A single reality disclosed through many faces.",
      items: [
        { name: "Mahādeva", script: "महादेव", sub: "The Great God", text: "Ascetic of Kailāsa — matted hair bearing moon and Gaṅgā, ash, serpent, tiger-skin, triśūla." },
        { name: "Naṭarāja", script: "नटराज", sub: "Lord of the Dance", text: "The cosmic dancer whose rhythm is creation, preservation and dissolution — a single pulse of reality." },
        { name: "Dakṣiṇāmūrti", script: "दक्षिणामूर्ति", sub: "The Silent Teacher", text: "Śiva as the guru who teaches through silence — knowledge transmitted beyond words." },
        { name: "Ardhanārīśvara", script: "अर्धनारीश्वर", sub: "Half-Woman Lord", text: "Śiva and Śakti as one body — the inseparability of consciousness and its power." },
        { name: "Bhairava", script: "भैरव", sub: "The Fierce", text: "The frightful form that dissolves the limited self; central to Kashmir Śaiva contemplation." },
        { name: "Liṅgodbhava", script: "लिङ्गोद्भव", sub: "The Infinite Pillar", text: "Śiva as the fiery column of light whose ends Brahmā and Viṣṇu cannot find — the limitless." },
        { name: "Tripurāntaka", script: "त्रिपुरान्तक", sub: "Destroyer of the Three Cities", text: "The burner of the three fortresses — read as the three bonds that limit the soul." },
        { name: "Sadāśiva", script: "सदाशिव", sub: "The Ever-Auspicious", text: "The supreme tattva where 'I am this' still holds the faintest trace of manifestation." },
      ],
    },
  ],
  sikhism: [
    {
      kind: "cards",
      title: "The Ten Gurus",
      note: "A lineage of teachers, culminating in the eternal Guru — the Granth.",
      items: [
        { name: "Guru Nanak", sub: "1469–1539", text: "The founder: one formless reality, the equality of all, and devotion lived through honest work and service." },
        { name: "Guru Angad", sub: "2nd Guru", text: "Formalised the Gurmukhi script and the institution of langar, the community kitchen." },
        { name: "Guru Amar Das", sub: "3rd Guru", text: "Strengthened equality — challenging caste and the seclusion of women; expanded langar." },
        { name: "Guru Ram Das", sub: "4th Guru", text: "Founded Amritsar and composed hymns central to Sikh devotion." },
        { name: "Guru Arjan", sub: "5th Guru", text: "Compiled the Adi Granth and built the Harmandir Sahib; the first Sikh martyr." },
        { name: "Guru Hargobind", sub: "6th Guru", text: "Introduced mīrī-pīrī — temporal and spiritual authority held together." },
        { name: "Guru Har Rai", sub: "7th Guru", text: "A guru of compassion, healing, and care for the natural world." },
        { name: "Guru Har Krishan", sub: "8th Guru", text: "The child guru, remembered for selfless service during an epidemic." },
        { name: "Guru Tegh Bahadur", sub: "9th Guru", text: "Gave his life defending the freedom of others to practise their faith." },
        { name: "Guru Gobind Singh", sub: "10th Guru", text: "Founded the Khalsa and vested guruship in the Guru Granth Sahib — the eternal teacher." },
      ],
    },
    {
      kind: "cards",
      title: "Key Concepts",
      note: "The grammar of Sikh thought and practice.",
      items: [
        { name: "Ik Oankar", script: "ੴ", sub: "One Reality", text: "One, formless and beyond division — the ground of being with which devotion attunes us." },
        { name: "Naam", script: "ਨਾਮ", sub: "The Divine Name", text: "Remembrance (naam simran) that turns the mind toward the one reality." },
        { name: "Hukam", script: "ਹੁਕਮ", sub: "Divine Order", text: "The order of reality; wisdom is living in harmony with it rather than ego's demands." },
        { name: "Haumai", script: "ਹਉਮੈ", sub: "Ego", text: "The self-centredness that separates us from the divine and from one another." },
        { name: "Seva", script: "ਸੇਵਾ", sub: "Selfless Service", text: "Service without expectation — enacted daily in the langar where all sit as equals." },
        { name: "Miri-Piri", script: "ਮੀਰੀ-ਪੀਰੀ", sub: "Temporal & Spiritual", text: "The integration of worldly responsibility and spiritual life — the saint-soldier." },
        { name: "Sangat", script: "ਸੰਗਤ", sub: "Community", text: "The holy congregation in which devotion and equality are practised together." },
        { name: "Khalsa", script: "ਖਾਲਸਾ", sub: "The Pure", text: "The community of the committed, founded by Guru Gobind Singh in 1699." },
      ],
    },
  ],
};
